import {Alert, Form, FormField, FormSubmitHandler} from 'platform/components';
import {Grid, Show, VStack} from 'platform/foundation';
import {z} from 'zod';

import {useGetBranchListQuery} from '@dms/api/core';
import {useGetRolesQuery} from '@dms/api/security';
import i18n from '@dms/i18n';
import {SettingsFooter} from '@dms/settings/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {MAX_TOKEN_LIFETIME_OPTIONS} from '../constants/MAX_TOKEN_LIFETIME_OPTIONS';

const SERVICE_ACCOUNT_NAME_MAX_LENGTH = 255;

const serviceAccountFormSchema = z.object({
  name: z.string().min(1).max(SERVICE_ACCOUNT_NAME_MAX_LENGTH),
  description: z.string().optional(),
  email: z.string().min(1).email(),
  maxTokenLifetime: z.string().min(1),
  roles: z.array(z.string()).min(1),
  branches: z.array(z.string()).min(1),
});

export type ServiceAccountFormValues = z.infer<typeof serviceAccountFormSchema>;

interface ServiceAccountFormProps extends RequiredTestIdProps {
  mode: 'create' | 'edit';
  defaultValues: ServiceAccountFormValues;
  submitButtonTitle: string;
  onSubmit: (values: ServiceAccountFormValues) => Promise<void>;
  onCancel: () => void;
}

export function ServiceAccountForm(props: ServiceAccountFormProps) {
  const {data: roles, isLoading: areRolesLoading} = useGetRolesQuery({});
  const {data: branchList, isLoading: areBranchesLoading} = useGetBranchListQuery();

  const isEdit = props.mode === 'edit';

  const roleOptions = (roles ?? []).map((role) => ({value: role.id, label: role.title}));
  const branchOptions = (branchList?.branchListItems ?? []).map((branch) => ({
    value: branch.id,
    label: branch.marketingName ?? branch.companyName ?? branch.id,
  }));

  const handleSubmit: FormSubmitHandler<ServiceAccountFormValues> = async (values) => {
    await props.onSubmit(values);
  };

  return (
    <Form<ServiceAccountFormValues>
      experimentalZodSchema={serviceAccountFormSchema}
      defaultValues={props.defaultValues}
      onSubmit={handleSubmit}
    >
      {(control) => (
        <VStack spacing={4}>
          <Show when={!isEdit}>
            <Alert
              variant="info"
              title={i18n.t('entity.serviceAccount.labels.technicalIdentityTitle')}
              message={i18n.t('entity.serviceAccount.labels.technicalIdentityDescription')}
              data-testid={suffixTestId('technicalIdentityInfo', props)}
            />
          </Show>
          <Grid columns={2} spacing={4}>
            <FormField
              control={control}
              name="name"
              type="text"
              isRequired
              label={i18n.t('entity.serviceAccount.labels.name')}
              data-testid={suffixTestId('name', props)}
            />
            <FormField
              control={control}
              name="maxTokenLifetime"
              type="choice"
              isRequired
              isNotClearable
              isDisabled={isEdit}
              options={MAX_TOKEN_LIFETIME_OPTIONS}
              label={i18n.t('entity.serviceAccount.labels.maxTokenLifetime')}
              helperText={i18n.t('entity.serviceAccount.labels.maxTokenLifetimeHelper')}
              data-testid={suffixTestId('maxTokenLifetime', props)}
            />
          </Grid>
          <FormField
            control={control}
            name="description"
            type="textarea"
            label={i18n.t('entity.serviceAccount.labels.description')}
            data-testid={suffixTestId('description', props)}
          />
          <FormField
            control={control}
            name="email"
            type="email"
            isRequired
            isDisabled={isEdit}
            label={i18n.t('entity.serviceAccount.labels.email')}
            helperText={i18n.t('entity.serviceAccount.labels.emailHelper')}
            data-testid={suffixTestId('email', props)}
          />
          <FormField
            control={control}
            name="roles"
            type="multiChoice"
            isRequired
            isLoading={areRolesLoading}
            options={roleOptions}
            label={i18n.t('entity.serviceAccount.labels.roles')}
            helperText={i18n.t('entity.serviceAccount.labels.rolesHelper')}
            data-testid={suffixTestId('roles', props)}
          />
          <FormField
            control={control}
            name="branches"
            type="multiChoice"
            isRequired
            isLoading={areBranchesLoading}
            options={branchOptions}
            label={i18n.t('entity.serviceAccount.labels.branches')}
            helperText={i18n.t('entity.serviceAccount.labels.branchesHelper')}
            data-testid={suffixTestId('branches', props)}
          />
          <SettingsFooter
            actions={[
              {
                type: 'button',
                variant: 'secondary',
                title: i18n.t('general.actions.cancel'),
                onClick: props.onCancel,
                'data-testid': suffixTestId('cancel', props),
              },
              {
                type: 'form-button',
                control,
                buttonType: 'submit',
                variant: 'primary',
                title: props.submitButtonTitle,
                'data-testid': suffixTestId('submit', props),
              },
            ]}
            data-testid={suffixTestId('footer', props)}
          />
        </VStack>
      )}
    </Form>
  );
}
